import isNil from 'lodash/isNil';

import { abbrNum } from './helper/common';
import { insertIf } from './list';
import {
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT
} from './deliverables';

import type {
  ChannelTypes,
  CustomReportDownloadType,
  PieChartStyles,
  ReportPostType
} from 'types';

import type {
  ReportingDeliverableTypes,
  ReportingMetaInfoIGStorySnippetType
} from 'modules/reporting/types';

type ReportFormat = {
  [type: string]: {
    label: CustomReportDownloadType,
    ext: string,
    trackingEvent: string
  }
}

export type ReportingType = 'instagram' | 'tiktok';

export const REPORT_POST_TYPE_TO_ICON_NAME = {
  instagram_post: 'instagram-custom-fg',
  instagram_story: 'story-custom-fg',
  youtube_video: 'youtube-custom-fg'
};

export const FORMAT_MAP: ReportFormat = {
  spreadsheet: {
    label: 'spreadsheet',
    ext: 'xlsx',
    trackingEvent: 'excel-report-downloaded'
  },
  presentation: {
    label: 'presentation',
    ext: 'pptx',
    trackingEvent: 'ppt-report-downloaded'
  }
};

export const REPORT_STATUS = {
  MISSING: 'missing',
  GENERATED: 'generated',
  PROGRESSING: 'progressing'
};

export const COLORS = {
  instagram_post: {
    main: '#4b98e3'
  },
  instagram_story: {
    main: '#3dd0f3'
  },
  instagram_reel: {
    main: '#0a66a9'
  },
  tiktok_video: {
    main: '#b82f6b'
  },
  youtube_video: {
    main: '#d482a6'
  },
  zalando_outfit: {
    main: '#ff6900',
    all: '#ffcf00',
    review: '#f39260',
    approved: '#f7aa0f',
    unpublished: '#ffe2b6',
    offline: '#cc7a00'
  },
  comments: {
    main: '#c5def6',
    tiktok_video: '#d482a6',
    instagram_reel: '#6ca3cb'
  },
  likes: {
    main: '#6fade9',
    instagram_reel: '#0a66a9',
    tiktok_video: '#c65989'
  },
  visits: {
    main: '#edf4fc',
    instagram_story: '#dbf4fc'
  },
  shares: {
    main: '#dbeaf9',
    tiktok_video: '#e3acc4',
    instagram_story: '#65bedb',
    instagram_reel: '#3b85ba'
  },
  saves: {
    main: '#a9cef2',
    instagram_reel: '#9dc2dd'
  },
  negative: {
    main: '#6fade9',
    tiktok_video: '#e3acc4'
  },
  positive: {
    main: '#c6def6',
    tiktok_video: '#c65989'
  },
  sentimentNeutral: {
    main: '#a9cef2',
    tiktok_video: '#d482a6'
  },
  blog: {
    main: '#4a4a4a'
  },
  other: {
    main: '#8d8d8d'
  },
  replies: {
    main: '#71d4f4'
  },
  stickerClicks: {
    main: '#94dff7'
  },
  linkClicks: {
    main: '#b8e9f9'
  },
  neutral: '#f5f5f5',
  websiteClicks: {
    main: '#edf9fd'
  }
};

export const REPORTING_TASK_TYPE_ORDER = [
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_OTHER
];

export const SUPPORTED_POST_TASK_TYPES: Array<ReportPostType> = [
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_TIKTOK_VIDEO
];

export const getSupportedTaskTypes = (
  isSCP: boolean
): Array<ReportingDeliverableTypes> => [
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  ...insertIf(isSCP, DELIVERABLE_TYPE_ZALANDO_OUTFIT)
];

export const getDisplayRate = (rate: number): number | string => {
  if (rate >= 1000) {
    abbrNum(rate.toFixed(1), { decimal: 1 });
  }

  return rate.toFixed(1);
};

export const getReportingPieChartStyles = (): PieChartStyles => ({
  metric: {
    fontSize: 24,
    lineHeight: 24,
    verticalAlign: 'top',
    align: 'center'
  },
  label: {
    padding: [0, 0, 8, 0],
    fontSize: 14,
    align: 'center',
    verticalAlign: 'top'
  },
  labelLine: {
    length: 28,
    length2: 6
  }
});

export const getAvailableChannels = (deliverableTypes: Array<ReportingDeliverableTypes>): Array<ReportingType> => [
  ...insertIf(
    deliverableTypes.includes(DELIVERABLE_TYPE_INSTAGRAM_POST) ||
    deliverableTypes.includes(DELIVERABLE_TYPE_INSTAGRAM_REEL) ||
    deliverableTypes.includes(DELIVERABLE_TYPE_INSTAGRAM_STORY),
    'instagram'
  ),
  ...insertIf(
    deliverableTypes.includes(DELIVERABLE_TYPE_TIKTOK_VIDEO),
    'tiktok'
  )
];

export const getIGStorySnippetTypeKey = (
  igStorySnippetType?: ReportingMetaInfoIGStorySnippetType
): string | null => {
  if (isNil(igStorySnippetType)) {
    return '';
  }

  if (igStorySnippetType === 'highest_of_snippets') {
    return 'highest';
  }

  if (igStorySnippetType === 'average_of_snippets') {
    return 'average';
  }

  return 'total';
};

export function getChannelByContentType(deliverableType: string): ChannelTypes {
  if (deliverableType.startsWith('youtube')) {
    return 'youtube';
  }

  if (deliverableType.startsWith('tiktok')) {
    return 'tiktok';
  }

  return 'instagram';
}

export function isZalandoOutfitOnlyCampaign(types: Array<ReportingDeliverableTypes>): boolean {
  if (types.length > 1) {
    return false;
  }

  return types.includes('zalando_outfit');
}
